'use client';

import { stateToUpdateRequest } from '@/app/(intakeFormV2)/intake-v2/hooks/useWizardSessionLifecycle';
import {
  WizardAction,
  WizardState,
} from '@/app/(intakeFormV2)/intake-v2/types';
import { devLog } from '@/lib/logging';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, type Dispatch } from 'react';
import { INTAKE_STEPS } from '../config/steps';
import {
  createSession,
  fetchSession,
  useUpdateSession,
} from '../contexts/intakeSessionApi';
import {
  getStoredSessionId,
  isValidSessionId,
  SESSION_ID_KEY,
  storeSessionId,
} from '../contexts/intakeSessionIdStorage';
import { intakeInitialWizardState } from '../contexts/WizardProvider';
import { restoreFromSession } from '../contexts/wizardReducer';

const SAVE_DEBOUNCE_TIME_MS = 1250;

const MEMBER_DETAILS_INDEX = INTAKE_STEPS.findIndex(
  s => s.id === 'member-details'
);
const QUESTIONNAIRE_STEP_IDS = INTAKE_STEPS.slice(0, MEMBER_DETAILS_INDEX).map(
  s => s.id
);
const POST_QUESTIONNAIRE_SLUGS = INTAKE_STEPS.slice(MEMBER_DETAILS_INDEX).map(
  s => s.slug
);

const isQuestionnaireComplete = (completedSteps: string[]): boolean =>
  QUESTIONNAIRE_STEP_IDS.every(id => completedSteps.includes(id));

const isOnQuestionnaireStep = (): boolean =>
  !POST_QUESTIONNAIRE_SLUGS.some(slug =>
    window.location.pathname.endsWith(`/${slug}`)
  );

export const useWizardSessionLifecycle = (
  state: WizardState,
  dispatch: Dispatch<WizardAction>
) => {
  const { update: updateSession } = useUpdateSession();
  const router = useRouter();

  const isInitialized = useRef(false);
  const isSavingToApi = useRef(false);
  const lastSavedState = useRef<string>('');

  const saveToApi = useCallback(
    async (currentState: WizardState) => {
      if (!currentState.sessionId || isSavingToApi.current) {
        return;
      }

      const stateJson = JSON.stringify(stateToUpdateRequest(currentState));

      // Skip if state hasn't changed
      if (stateJson === lastSavedState.current) {
        return;
      }

      isSavingToApi.current = true;

      dispatch({ type: 'SET_SAVING', payload: true });

      try {
        await updateSession(
          currentState.sessionId,
          stateToUpdateRequest(currentState)
        );

        lastSavedState.current = stateJson;
      } catch (error) {
        console.error('Error saving intake session to API:', error);

        dispatch({
          type: 'SET_ERROR',
          payload: 'Error communicating with the server.',
        });
      } finally {
        isSavingToApi.current = false;

        dispatch({ type: 'SET_SAVING', payload: false });
      }
    },
    [dispatch, updateSession]
  );

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    const initialize = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Allow forcing a new session via ?new_session query param
        const params = new URLSearchParams(window.location.search);

        if (params.has('new_session')) {
          localStorage.removeItem(SESSION_ID_KEY);

          params.delete('new_session');

          const newUrl =
            window.location.pathname +
            (params.size ? `?${params}` : '') +
            window.location.hash;

          window.history.replaceState({}, '', newUrl);
        }

        const storedSessionId = getStoredSessionId();

        if (storedSessionId) {
          const apiSession = await fetchSession(storedSessionId);

          if (apiSession) {
            dispatch({ type: 'RESTORE_SESSION', payload: apiSession });

            lastSavedState.current = JSON.stringify(
              stateToUpdateRequest(restoreFromSession(apiSession))
            );

            if (
              isQuestionnaireComplete(apiSession.completedSteps ?? []) &&
              isOnQuestionnaireStep()
            ) {
              router.replace('/get-started/member-details');
            }

            dispatch({ type: 'SET_LOADING', payload: false });

            return;
          }

          console.error(
            'Session not found in API, creating new session:',
            storedSessionId
          );
        }

        const newSession = await createSession('intake_v2.5');

        if (!isValidSessionId(newSession.sessionId)) {
          throw new Error('Session creation returned invalid session ID');
        }

        dispatch({ type: 'SET_SESSION_ID', payload: newSession.sessionId });

        storeSessionId(newSession.sessionId);

        lastSavedState.current = JSON.stringify(
          stateToUpdateRequest(intakeInitialWizardState)
        );
      } catch (error) {
        console.error('Error initializing intake session:', error);

        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to initialize session. Please try again.',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initialize().then(() => (isInitialized.current = true));
  }, [dispatch, router]);

  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }

    if (state.error) return;

    const timeout = setTimeout(() => saveToApi(state), SAVE_DEBOUNCE_TIME_MS);

    return () => clearTimeout(timeout);
  }, [state, saveToApi]);

  useEffect(() => {
    if (isInitialized.current) {
      devLog('Wizard state updated:', state);
    }
  }, [state]);
};
